import { useState, useRef, useEffect } from 'react';
import type React from 'react';
import {
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Button,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import ToggleArrowsUpAndDown from '../ToggleArrowsUpAndDown';

interface DropdownOption {
    value: 'trend' | 'aggregated';
    label: React.ReactNode;
}

interface AggregatedResultsDropdownProps {
    onSelect: (mode: 'trend' | 'aggregated') => void;
    label?: string;
}

export const AggregatedResultsDropdown: React.FC<
    AggregatedResultsDropdownProps
> = ({ onSelect, label = 'Select' }) => {
    const options: DropdownOption[] = [
        {
            value: 'trend',
            label: (
                <FormattedMessage defaultMessage="Moving averrage + Trend" />
            ),
        },
        {
            value: 'aggregated',
            label: <FormattedMessage defaultMessage="Aggregated Average" />,
        },
    ];

    const [open, setOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(options[0]);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const [buttonWidth, setButtonWidth] = useState<number | null>(null);
    const menuWidthRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (menuWidthRef.current) {
            const menuWidth = menuWidthRef.current.offsetWidth;
            setButtonWidth(menuWidth + 42);
        }
    }, []);

    const handleToggle = () => {
        setOpen(!open);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current?.contains(event.target as Node)) {
            return;
        }
        setOpen(false);
    };

    const handleSelect = (option: DropdownOption) => {
        setSelectedOption(option);
        onSelect(option.value);
        setOpen(false);
    };

    return (
        <div>
            <Button
                ref={anchorRef}
                onClick={handleToggle}
                variant="outlined"
                sx={(theme) => ({
                    backgroundColor: theme.bino.primary,
                    color: theme.palette.background.paper,
                    width: buttonWidth,
                    padding: theme.spacing(1, 2),
                    whiteSpace: 'nowrap',
                    '&:hover': {
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.primary.contrastText,
                    },
                })}
                endIcon={
                    <ToggleArrowsUpAndDown
                        toggle={open}
                        size={12}
                        className="nope"
                    />
                }
            >
                {selectedOption ? selectedOption.label : label}
            </Button>
            <div ref={menuWidthRef}>
                <Popper
                    open={open}
                    transition
                    disablePortal
                    anchorEl={anchorRef.current}
                    role={undefined}
                    sx={{
                        zIndex: 1500,
                        width: buttonWidth,
                    }}
                >
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                            <Paper>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MenuList id="split-button-menu">
                                        {options.map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                onClick={() =>
                                                    handleSelect(option)
                                                }
                                                disabled={
                                                    selectedOption?.value ===
                                                    option.value
                                                }
                                                sx={{
                                                    color:
                                                        selectedOption?.value ===
                                                        option.value
                                                            ? 'grey.500'
                                                            : 'inherit',
                                                }}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </div>
        </div>
    );
};

export default AggregatedResultsDropdown;
